/* eslint-disable block-spacing, brace-style */
import {
    Avatar,
    Form,
    Input,
    Modal,
    Select,
    Space,
} from 'antd';
import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { startUpdateRide } from '../actions/ride';
import { getDeliveryProviders, getSmallFleets } from '../api/deliveryProviders';
import { findDriver } from '../api/drivers';
import { InternalProviders, Providers } from '../const/providers';
import { useQuery } from '../hooks/useQuery';
import { DeliveryType, SmallFleetType } from '../types/delivery';
import SearchInput from './SearchInput';

const { Option } = Select;

interface ModalFormProps {
  visible: boolean;
  onCancel: () => void;
}

interface FormValues {
  selectedProvider: Providers;
  deliveryFee: string;
  driver: { id: string, name: string };
  trackingLink: string;
  driverTag?: string;
}

const ModalForm = ({
    visible,
    onCancel,
}: ModalFormProps) => {
    const query = useQuery();
    const bookingID = query.get('id') || '';
    const [deliveries, setDeliveries] = useState<DeliveryType[]>([]);
    const [smallFleets, setSmallFleets] = useState<SmallFleetType[]>([]);
    const [form] = Form.useForm();
    const { loading } = useSelector((state: any) => state.ride);
    const dispatch = useDispatch();

    const selectedProvider = Form.useWatch('selectedProvider', form);
    const isSmallFleets = selectedProvider === 'SMALL_FLEETS';

    useEffect(() => {
        getDeliveryProviders()
            .then(({ data }: any) => setDeliveries(data.deliveryProviders))
            .catch(console.log);
        getSmallFleets(300)
            .then(({ data }: any) => {
                setSmallFleets(data.body.smallFleets);
            })
            .catch(console.log);
    }, []);

    const handleSubmit = async (values: FormValues) => {
        const payload: any = { ...values, driverName: values.driver.name, driverID: values.driver.id };
        const isSmallFleets = payload.selectedProvider === 'SMALL_FLEETS';
        if (!isSmallFleets) {
            delete payload.driverTag;
        }
        delete payload.driver;
        await dispatch(startUpdateRide(bookingID, payload));
        onCancel();
    };

    const getDriverByName = (name: string) => {
        const query = { search: name };
        return findDriver(query)
            .then(({ data }) => {
                const drivers = data.data.data;
                return drivers.map((driver: any) => ({
                    id: driver._id,
                    name: `${driver.name} ${driver.lastName}`,
                }));
            })
            .catch(console.log);
    };

    const resetDriverID = (selectedProvider: string) => {
        form.setFieldsValue({ driver: {}, selectedProvider });
    };

    return (
        <Modal
            visible={visible}
            title="Asignar proveedor"
            onCancel={onCancel}
            onOk={() => form.submit()}
            confirmLoading={loading}
        >
            <Form form={form} onFinish={handleSubmit} layout="vertical">
                <Form.Item name="selectedProvider" label="Proveedor">
                    <Select placeholder="Picker" onChange={resetDriverID}>
                        {deliveries.map((delivery) => (
                            <Option key={delivery.keyName} value={delivery.keyName} label={delivery.name}>
                                <Space>
                                    <Avatar size="small" src={delivery.image} />
                                    {delivery.name}
                                </Space>
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item shouldUpdate={(prev, curr) => prev.selectedProvider !== curr.selectedProvider} noStyle>
                    {isSmallFleets && (
                        <Form.Item name="driverTag" label="Flota">
                            <Select placeholder="Picker">
                                {smallFleets.map((smallFleet, index) => (
                                    <Option
                                        key={index}
                                        value={smallFleet.keyName}
                                        label={smallFleet.name}
                                    >
                                        {smallFleet.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                </Form.Item>
                <Form.Item name="deliveryFee" label="Precio del delivery">
                    <Input placeholder="0.10" />
                </Form.Item>
                <Form.Item shouldUpdate={(prev, curr) => prev.selectedProvider !== curr.selectedProvider} noStyle>
                    {() => {
                        const selectedProvider = form.getFieldValue('selectedProvider');
                        const isInternalProvider = InternalProviders.includes(selectedProvider);
                        return (
                            <Form.Item name="driver" label="Nombre del conductor" initialValue={{}}>
                                <SearchInput
                                    getData={isInternalProvider ? getDriverByName : null}
                                    optionKeys={['id', 'name']}
                                    searchKey="name"
                                    placeholder="Alejandro Sosa"
                                />
                            </Form.Item>
                        );
                    }}
                </Form.Item>
                <Form.Item name="trackingLink" label="URL del tracking">
                    <Input type="url" placeholder="https://picker.com/tracking/123ABC" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ModalForm;
